import React from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RootState } from '@/redux/store';
import { useToast } from '@/components/ui/use-toast';
import { useGetDashboardConfigQuery, useUpdateDashboardConfigMutation } from '@/services/dashboardConfigApi';
import {
  OperationsConfigFormData,
  operationsConfigSchema,
  defaultOperationsConfigValues,
} from '@/types/dashboard-config';
import ConfigCard from './components/ConfigCard';
import NumberInput from './components/NumberInput';
import { ThresholdGroup } from './components/ThresholdGroup';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { ErrorType } from '@/types';

interface OperationsSectionProps {
  refetch: () => void;
}

export default function OperationsSection({ refetch }: OperationsSectionProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const [updateConfig, { isLoading }] = useUpdateDashboardConfigMutation();
  const { data: configData } = useGetDashboardConfigQuery({ orgId });

  const form = useForm<OperationsConfigFormData>({
    mode: 'all',
    resolver: zodResolver(operationsConfigSchema),
    defaultValues: defaultOperationsConfigValues,
  });

  const { handleSubmit, formState: { errors }, reset, setValue, watch } = form;
  const [isEditing, setIsEditing] = React.useState(false);

  const resetToLoaded = React.useCallback(() => {
    if (configData?.dashboard_configuration) {
      const config = configData.dashboard_configuration;
      reset({
        operations_peak_hour_occupancy_red_cutoff: config.operations_peak_hour_occupancy_red_cutoff || '',
        operations_peak_hour_occupancy_amber_cutoff: config.operations_peak_hour_occupancy_amber_cutoff || '',
        operations_peak_hour_occupancy_green_cutoff: config.operations_peak_hour_occupancy_green_cutoff || '',
        operations_low_stock_threshold: config.operations_low_stock_threshold || '',
        operations_heatmap_lookback_period: config.operations_heatmap_lookback_period || '',
      });
    }
  }, [configData, reset]);

  React.useEffect(() => {
    resetToLoaded();
  }, [resetToLoaded]);

  const handleCancelEdit = React.useCallback(() => {
    setIsEditing(false);
    resetToLoaded();
  }, [resetToLoaded]);

  const onSubmit = async (formData: OperationsConfigFormData) => {
    try {
      const config: Record<string, string> = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          config[key] = String(value);
        }
      });

      await updateConfig({ dashboard_configuration: config }).unwrap();
      toast({ variant: 'success', title: 'Operational settings saved successfully' });
      setIsEditing(false);
      refetch();
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: 'destructive',
        title: 'Error',
        description: typedError.data?.detail || typedError.data?.message || 'Failed to update operational settings',
      });
    }
  };

  const onError = (fieldErrors: any) => {
    console.error('Operations form validation errors:', fieldErrors);
    toast({ variant: 'destructive', description: 'Please check the entered values and fix any validation errors' });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col gap-4">
      {/* Peak hour occupancy colors */}
      <ConfigCard
        title="Peak hour occupancy colors"
        description="Peak Hours Today grid. Cutoffs must increase from red to green."
      >
        <ThresholdGroup
          prefixAriaLabel="Peak hour occupancy"
          redValue={String(watch('operations_peak_hour_occupancy_red_cutoff') || '')}
          amberValue={String(watch('operations_peak_hour_occupancy_amber_cutoff') || '')}
          greenValue={String(watch('operations_peak_hour_occupancy_green_cutoff') || '')}
          onRedChange={(value) => setValue('operations_peak_hour_occupancy_red_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onAmberChange={(value) => setValue('operations_peak_hour_occupancy_amber_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onGreenChange={(value) => setValue('operations_peak_hour_occupancy_green_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          errors={{
            red: errors.operations_peak_hour_occupancy_red_cutoff?.message,
            amber: errors.operations_peak_hour_occupancy_amber_cutoff?.message,
            green: errors.operations_peak_hour_occupancy_green_cutoff?.message,
          }}
          isEditing={isEditing}
        />
      </ConfigCard>

      {/* Low stock threshold */}
      <ConfigCard
        title="Low stock threshold"
        description="Default across all items. Override per item from Inventory."
      >
        <div className="max-w-xs">
          {isEditing ? (
            <NumberInput
              aria-label="Low stock threshold"
              value={String(watch('operations_low_stock_threshold') || '')}
              onChange={(value) => setValue('operations_low_stock_threshold', value, { shouldValidate: true, shouldDirty: true })}
              unit="units"
              placeholder="Low stock threshold"
              min={1}
              error={errors.operations_low_stock_threshold?.message}
            />
          ) : (
            <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
              {String(watch('operations_low_stock_threshold') || 'Not set')}
            </div>
          )}
        </div>
      </ConfigCard>

      {/* Heatmap look-back period */}
      <ConfigCard
        title="Heatmap look-back period"
        description="How many past weeks the activity heatmap averages. Max 12."
      >
        <div className="max-w-xs">
          {isEditing ? (
            <NumberInput
              aria-label="Heatmap look-back period"
              value={String(watch('operations_heatmap_lookback_period') || '')}
              onChange={(value) => setValue('operations_heatmap_lookback_period', value, { shouldValidate: true, shouldDirty: true })}
              unit="weeks"
              placeholder="Heatmap look-back period"
              min={1}
              max={12}
              error={errors.operations_heatmap_lookback_period?.message}
            />
          ) : (
            <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
              {String(watch('operations_heatmap_lookback_period') || 'Not set')}
            </div>
          )}
        </div>
      </ConfigCard>

      {/* Action buttons */}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        {!isEditing ? (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={isLoading}
            className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            Edit
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={isLoading}
              className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
            >
              Cancel
            </button>

            <LoadingButton
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className="rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors"
            >
              Save changes
            </LoadingButton>
          </>
        )}
      </div>
    </form>
  );
}


